import type { EstimateStatus } from '@/features/consignment-form/estimate'
import { t } from '@/lang'
import { Banner } from '@/ui/Banner'
import { Button } from '@/ui/Button'
import { testIds } from '@/lib/test-ids'

type Props = {
  submittable: boolean
  submitting?: boolean
  /** Turns Submit into Retry, which resends the saved request instead of validating and submitting the form. */
  onRetry?: () => void
  estimateStatus: EstimateStatus
  estimatedTotal: number
}

// ponytail: local GBP format until f-edge sends a formatted total.
const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })

export function SubmitBar({ submittable, submitting = false, onRetry, estimateStatus, estimatedTotal }: Props) {
  return (
    <div className="mt-7.5 flex flex-col gap-5 md:flex-row md:items-center md:gap-7.5">
      <Banner className="md:grow">{t('submit.rate-note')}</Banner>
      <div className="flex items-center gap-7.5 md:contents">
        <p
          data-testid={testIds.consignmentForm.estimate}
          className="flex-1 text-end md:flex-none md:shrink-0 md:text-center"
        >
          <span aria-live="polite" className="block text-h2 whitespace-nowrap text-primary-700">
            {estimateStatus === 'ready' ? gbp.format(estimatedTotal) : t('common.no-value')}
          </span>
          <span className="block text-xs font-medium text-primary-700">
            {estimateStatus === 'loading'
              ? t('submit.estimate-loading')
              : estimateStatus === 'unavailable'
                ? t('submit.estimate-unavailable')
                : t('submit.estimate')}
          </span>
        </p>
        <Button
          type={onRetry ? 'button' : 'submit'}
          onClick={onRetry}
          size="xl"
          disabled={!submittable || submitting}
          data-testid={testIds.consignmentForm.submit}
          className="flex-1 md:flex-none"
        >
          {submitting ? t('submit.submitting') : onRetry ? t('submit.retry') : t('submit.submit')}
        </Button>
      </div>
    </div>
  )
}
